import React from 'react';
import { useGeo } from '../contexts/GeoContext';

/**
 * Concise, geo-aware VAT/import notice.
 *
 *   variant="footer"  — single-line footer disclosure
 *   variant="cart"    — short banner for enquiry / checkout modals
 *   variant="full"    — full plain-English breakdown for the /pricing-vat page
 */
const VatNotice = ({ variant = 'footer' }) => {
  const { isUK, isEU } = useGeo();

  if (variant === 'footer') {
    return (
      <p className="text-xs text-gray-400 leading-relaxed max-w-3xl">
        Prices shown exclude VAT. UK VAT at 20% is added at checkout for UK deliveries.
        {' '}For Margin-Scheme works, no further VAT is charged.
        {' '}International buyers may be subject to local import duties.
        {' '}<a href="/pricing-vat" className="underline hover:text-white">Pricing &amp; tax details</a>.
      </p>
    );
  }

  if (variant === 'cart') {
    return (
      <div className="bg-amber-50 border border-amber-200 text-amber-900 text-sm p-4 rounded-sm space-y-1.5">
        <p className="font-medium">Pricing &amp; tax</p>
        {isUK ? (
          <p className="text-xs leading-relaxed">UK delivery — 20% UK VAT is added at checkout (except works sold under the VAT Margin Scheme, where no further VAT is chargeable).</p>
        ) : isEU ? (
          <p className="text-xs leading-relaxed">EU delivery — UK VAT does not apply. Local import VAT and duties may be payable on receipt in your country.</p>
        ) : (
          <p className="text-xs leading-relaxed">International delivery — UK VAT does not apply (zero-rated for export). Local import duties or sales tax may be payable on receipt.</p>
        )}
      </div>
    );
  }

  // variant="full"
  return (
    <div className="max-w-none text-neutral-700 leading-relaxed [&_p]:mb-4 [&_h3]:text-lg [&_h3]:font-medium [&_h3]:text-black [&_h3]:mt-10 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-4 [&_li]:mb-2">
      <p>All prices on Zebra One Gallery are quoted in GBP (£). Two VAT regimes apply, depending on the work:</p>
      <h3>1. Standard rate works</h3>
      <p>For most artworks (Studwell, Sunday B. Morning, contemporary photography and the wider catalogue), UK VAT at 20% applies.</p>
      <ul>
        <li><strong>UK customers</strong> — prices shown exclude VAT. 20% UK VAT is added at checkout when the work is delivered in the UK.</li>
        <li><strong>EU customers</strong> — UK VAT is not charged — you pay the price shown. Your country may charge import VAT and duties on receipt of the work.</li>
        <li><strong>USA &amp; rest of the world</strong> — exports are zero-rated for UK VAT, so no VAT is charged. Local sales tax / import duties may apply on receipt.</li>
      </ul>
      <h3>2. VAT Margin-Scheme works</h3>
      <p>Some works (typically secondary-market originals — for example our Francis Bacon, Joan Miró and Pablo Picasso lithographs) are sold under HMRC's VAT Margin Scheme. Under this scheme:</p>
      <ul>
        <li>The published price is the all-in price for any buyer.</li>
        <li>No further VAT is added at checkout, regardless of where the buyer is.</li>
        <li>The work's price detail will read <em>"VAT Margin Scheme — no further VAT"</em>.</li>
      </ul>
      <h3>Shipping</h3>
      <p>Shipping is quoted bespoke for each work and country, calculated by our framing partners and specialist art-shippers. We will email a quote on receipt of your enquiry.</p>
      <h3>Import duties</h3>
      <p>Import VAT, customs duty and any local sales taxes that arise on receipt of the work in the destination country are the responsibility of the buyer. Zebra One Gallery is happy to provide all paperwork required for clearance.</p>
    </div>
  );
};

export default VatNotice;
